import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'

import { colors } from 'Constants'
import { Flex } from 'lese'

import request from 'Root/request'

import Card from 'Components/Card'
import { AutoComplete } from 'Components/Input'
import DateSelector from 'Components/DateSelector'
import Switch from 'react-switch'
import { SectionHeader } from 'Components/Section'
import Button from 'Components/Button'

const ModalCard = styled(Card)`
  background: ${colors.background};
  min-width: 350px;
`

const ErrorMessage = styled.span`
  color: #f00;
`

const Label = styled(Flex)``
Label.withComponent('label')

const createValidater = ({ validater, errorMessage, setError, setValid }) => {
  return (toValidate, onlyPositive) => {
    const isValid = validater(toValidate)
    if (onlyPositive) {
      if (isValid) setError('')
      setValid(isValid)
      return isValid
    }

    if (!isValid) setError(errorMessage)
    else setError('')

    setValid(isValid)
    return isValid
  }
}

export default ({
  baseUrl,
  isCluster,
  isOffice,
  isPatient,
  color,
  ...props
}) => {
  const [appCode, setAppCode] = useState('')
  const [office, setOffice] = useState('')
  const [patientRef, setPatientRef] = useState('')
  const [dates, setDates] = useState({})
  const [showArchivedOffices, setShowArchivedOffices] = useState(undefined)

  const [appCodeError, setAppCodeError] = useState('')
  const [officeError, setOfficeError] = useState('')
  const [patientRefError, setPatientRefError] = useState('')

  const [appCodeSuggestions, setAppCodeSuggestions] = useState([])
  const [officeSuggestions, setOfficeSuggestions] = useState([])
  const [patientRefSuggestions, setPatientRefSuggestions] = useState([])

  const [valid, setValid] = useState(false)

  useEffect(() => {
    setValid(false)
    request('/info/app-codes/list').then(res => setAppCodeSuggestions(res.data))
  }, [])

  const validateAppCode = createValidater({
    validater: appCode => appCodeSuggestions.includes(appCode),
    setError: setAppCodeError,
    errorMessage: 'Invalid App Code provided',
    setValid
  })

  const validateOffice = createValidater({
    validater: office =>
      officeSuggestions.map(val => val.title).includes(office),
    setError: setOfficeError,
    errorMessage: 'Invalid Office provided',
    setValid
  })

  const validatePatientRef = createValidater({
    validater: patientRef => patientRefSuggestions.includes(patientRef),
    setError: setPatientRefError,
    errorMessage: 'Invalid Patient Id provided',
    setValid
  })

  const updateOfficeSuggestions = appCode =>
    request(`/info/offices/list?appCode=${appCode}`).then(res =>
      setOfficeSuggestions(res.data)
    )

  const updatePatientRefSuggestions = office =>
    request(
      `/info/patients/list?officeId=${
        officeSuggestions.find(val => val.title === office)?.id
      }`
    ).then(res => setPatientRefSuggestions(res.data))

  const url = generateUrl({
    baseUrl,
    isCluster,
    appCode,
    dates,
    officeId: officeSuggestions.find(val => val.title === office)?.id,
    patientRef,
    showArchivedOffices
  })

  if (isCluster)
    return (
      <ModalCard href={url} color={color} useButton={false} {...props}>
        <Flex column separation="24px" xAlign="stretch">
          <Flex column separation="12px">
            <SectionHeader>Date Range:</SectionHeader>
            <DateSelector onChange={setDates} value={dates} color={color} />
          </Flex>
        </Flex>
        <Button color={color} href={url}>
          Create Report
        </Button>
      </ModalCard>
    )

  return (
    <ModalCard href={url} color={color} useButton={false} {...props}>
      <Flex column separation="24px" xAlign="stretch">
        <Flex column separation="12px">
          <SectionHeader>Date Range:</SectionHeader>
          <DateSelector onChange={setDates} value={dates} color={color} />
        </Flex>
        <Flex column separation="12px">
          <SectionHeader>Select App Code:</SectionHeader>
          <AutoComplete
            value={appCode}
            onChange={appCode => {
              setAppCode(appCode)
              if (validateAppCode(appCode, true))
                updateOfficeSuggestions(appCode)
            }}
            onBlur={() => validateAppCode(appCode)}
            suggestions={appCodeSuggestions}
            placeholder="Start typing your App Code"
          />
          {appCodeError.length > 0 && (
            <ErrorMessage>{appCodeError}</ErrorMessage>
          )}
        </Flex>
      </Flex>
      {isOffice && (
        <Flex column separation="12px">
          <SectionHeader>Select Office:</SectionHeader>
          <AutoComplete
            disabled={!(appCode.length > 0) || appCodeError.length > 0}
            value={office}
            onChange={office => {
              setOffice(office)
              if (validateOffice(office, true))
                updatePatientRefSuggestions(office)
            }}
            suggestions={officeSuggestions
              .filter(val =>
                showArchivedOffices ? true : val.archived === 'N'
              )
              .map(val => val.title)}
            placeholder="Start typing your Office"
          />
          {officeError.length > 0 && <ErrorMessage>{officeError}</ErrorMessage>}
        </Flex>
      )}
      {isPatient && (
        <Flex column separation="12px">
          <SectionHeader>Select Patient:</SectionHeader>
          <AutoComplete
            disabled={
              !(office.length > 0) ||
              officeError.length > 0 ||
              !(appCode.length > 0) ||
              appCodeError.length > 0
            }
            value={patientRef}
            onChange={patientRef => {
              setPatientRef(patientRef)
              validatePatientRef(patientRef, true)
            }}
            suggestions={patientRefSuggestions}
            placeholder="Start typing your Patient Id"
          />
          {patientRefError.length > 0 && (
            <ErrorMessage>{patientRefError}</ErrorMessage>
          )}
        </Flex>
      )}
      <Label separation="12px" yAlign="center">
        <Switch
          onChange={setShowArchivedOffices}
          checked={showArchivedOffices}
          uncheckedIcon={false}
          checkedIcon={false}
        />
        <span>Show Archived Offices</span>
      </Label>
      <Button disabled={!valid} href={url} color={color}>
        Create Report
      </Button>
    </ModalCard>
  )
}

function generateUrl({
  baseUrl,
  isCluster,
  appCode,
  dates,
  officeId,
  patientRef,
  showArchivedOffices
}) {
  let url = `${baseUrl}/${appCode}`
  const query = {
    startDate: dates?.startDate?.getTime(),
    endDate: dates?.endDate?.getTime(),
    isCluster,
    officeId,
    patientRef,
    showArchivedOffices
  }

  for (const key in query) {
    if (query[key] === undefined || query[key] === '') delete query[key]
  }

  const serializedQuery = new URLSearchParams(query).toString()
  if (serializedQuery.length > 0) url += '?' + serializedQuery

  return url
}
